import { IFrameWrapper, Spinner } from '@memoresa/ui-components';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import qs from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useMutation } from 'react-query';
import { ActionsDrawer } from '../../../components/dialogs/productDetailsPage/ActionsDrawer';
import { ProductWithoutVariants } from '../../../components/productDetails/ProductWithoutVariants';
import { ProductWithVariants } from '../../../components/productDetails/ProductWithVariants';
import { ImageSlider } from '../../../components/slider/ImageSlider';
import { useCartContext } from '../../../context/cart/useCartContext';
import * as ga from '../../../lib/ganalytics';
import { OrdersService } from '../../../services/order/OrdersService';
import { productKeys } from '../../../utils/consts/productKeys';
import { checkApiStatus } from '../../../utils/functions/checkApiStatus';
import { isEmpty } from '../../../utils/functions/isEmpty';
import { useFocusFirstError } from '../../../utils/hooks/useFocusFirstError';
import { useGetSessionStorage } from '../../../utils/hooks/useGetSessionStorage';
import { useSetLocale } from '../../../utils/hooks/useSetLocale';
import { useStaticCookies } from '../../../utils/hooks/useStaticCookies';
import {
  ProductDetails,
  ProductDetailsWithVariants,
} from '../../../utils/interfaces/ProductDetails';

const ProductDetailsPage = ({ product }: { product: ProductDetails }) => {
  useSetLocale();
  useGetSessionStorage();
  const { cookieConsent } = useStaticCookies();
  const router = useRouter();
  const { slug, epId } = router.query;
  const { addProduct } = useCartContext();
  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      selectedVariant: product.variants?.find(variant => variant.default)?.id,
      email: '',
      name: '',
      epId: '',
    },
  });
  useFocusFirstError(methods);
  const watchFields = useWatch({ control: methods.control });
  //Need this to set the image since the images are fetched on the <ImageSlider /> component
  const [mainImage, setMainImage] = useState<string>();
  const [openDrawer, setOpenDrawer] = useState(false);

  const { mutate: checkSubscription } = useMutation(
    ['checkSubscription'],
    () => {
      var o = Object.fromEntries(
        product.customizationFields?.map((field, i) => [
          field.name,
          Object.values(watchFields)[i],
        ])
      );
      return OrdersService.checkSubscription({
        productId: product.id,
        customFields: o,
      });
    },
    {
      onSuccess: data => {
        if (!data.checkResult) {
          methods.setError('email', { message: data.errorMessage });
        } else {
          handleBuyAction({ currentAmount: 1 });
        }
      },
      onError(e: any) {
        methods.setError('email', { message: e.message });
      },
    }
  );

  // removal of directBy property from localStorage upon page loading
  useEffect(() => {
    localStorage.removeItem('directBuy');
  }, []);

  // resetting the form field references after a new product has been loaded
  //  -> otherwise the mutation hook for subscription checks would fail because the input fields of previously watched products would still be in the form field list.
  useEffect(() => {
    methods.reset();
  }, [product]); //eslint-disable-line

  useEffect(() => {
    const values = Object.keys(watchFields).map(key => watchFields[key]);
    if (
      //TODO: maybe the product keys should be updated
      slug === productKeys.EMERGENCY_CARD &&
      values[0] &&
      values[0].length === 1
    ) {
      ga.event({
        action: 'fill-in-information',
        category: 'buy-item',
        label: 'input-card-owner',
      });
    }
  }, [watchFields]); //eslint-disable-line

  useEffect(() => {
    if (!product.customizationFields?.find(cF => cF.name === 'epId')) {
      return;
    }
    methods.setValue('epId', epId as string);
  }, [epId, product]); //eslint-disable-line

  const handleAction = async ({
    product,
    type,
    currentAmount,
  }: {
    product: ProductDetails;
    type: 'buy' | 'addToCart';
    currentAmount: number;
  }) => {
    ga.event({
      action: type === 'buy' ? `click-buy-now` : 'click-add-to-cart',
      category: 'buy-item',
      label: `add-${slug}`,
    });
    await methods.trigger();
    if (
      !methods.formState.errors ||
      isEmpty(methods.formState.errors) ||
      methods.formState.errors.name?.type === 'pattern' ||
      methods.formState.errors.name?.type === 'maxLength'
    ) {
      if (type === 'addToCart') {
        addProduct({
          id: product.id,
          image: mainImage ?? '',
          name: product.title,
          basePrice: product.priceInfo.priceInEurCents,
          volumeDiscountSettings: product.priceInfo.volumeDiscountSettings,
          stackable: product.stackable,
          details:
            product.customizationFields && product.customizationFields?.length
              ? product.customizationFields?.map(field => {
                  return {
                    name: field.label,
                    value: watchFields[field.name],
                    id: field.name,
                  };
                })
              : undefined,
          amount: currentAmount,
          amountStepping: product.amountSelectorConfig.stepping,
          maxAmount: product.amountSelectorConfig?.max ?? 999,
          minAmount: product.amountSelectorConfig.min,
        });
        setOpenDrawer(true);
        //Reset the input field values
        if (product?.customizationFields?.length > 0) {
          product.customizationFields?.forEach(element => {
            methods.resetField(element.name as any);
          });
        }
      }
      if (type === 'buy') {
        if (product.priceInfo.paymentType === 'SUBSCRIPTION' && product.requiresSubscriptionCheck) {
          var o = Object.fromEntries(
            product.customizationFields?.map((field, i) => [
              field.name,
              Object.values(watchFields)[i],
            ])
          );
          checkSubscription();
        } else {
          handleBuyAction({ currentAmount });
        }
      }
    }
  };
  const handleBuyAction = ({ currentAmount }: { currentAmount: number }) => {
    localStorage.setItem(
      'directBuy',
      JSON.stringify({
        id: (isProductWithVariants) ? methods.getValues('selectedVariant') : product.id,
        image: mainImage ?? '',
        name: `${product.title}`,
        details:
          product.customizationFields && product.customizationFields.length
            ? product.customizationFields?.map(field => {
                return {
                  name: field.label,
                  value: watchFields[field.name],
                  id: field.name,
                };
              })
            : undefined,
        amount: currentAmount,
      })
    );
    router.push(
      {
        pathname: '/shop/checkout',
        query: {
          action: 'directBuy',
        },
      },
      '/shop/checkout'
    );
  };

  const isProductWithVariants = useMemo(() => {
    return product.variants && product.variants.length > 0;
  }, [product]);

  if (router.isFallback)
    return (
      <div className="flex items-center justify-center w-full h-full">
        <Spinner />
      </div>
    );
  return (
    <>
      <FormProvider {...methods}>
        <ActionsDrawer open={openDrawer} setIsOpen={setOpenDrawer} />
        <div className="flex flex-col w-full h-full space-y-8 sm:mt-0">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:grid sm:grid-cols-5">
            <div className="sm:col-span-2">
              <div className="h-[300px] sm:max-h-full sm:h-full">
                <ImageSlider
                  images={product.images}
                  setMainImage={setMainImage}
                />
              </div>
            </div>
            {isProductWithVariants ? (
              <ProductWithVariants
                product={product as ProductDetailsWithVariants}
                handleAction={handleAction}
              />
            ) : (
              <ProductWithoutVariants
                product={product}
                handleAction={handleAction}
              />
            )}
          </div>

          {cookieConsent && (
            <IFrameWrapper
              url={`${product.descriptionURL}${
                cookieConsent === 'all' ? '?cookies=true' : '?cookies=false'
              }`}
            />
          )}
        </div>
      </FormProvider>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  params,
  locale,
  query,
}) => {
  try {
    const queries = qs.stringify({ campaign: query.m_campaign ?? query.ref });
    const url = `${process.env.API_HOST}/shop/products/${params!.slug}${
      queries && `?${queries}`
    }`;
    const product = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Timezone: new Date().getTimezoneOffset().toString(),
        TimezoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
        'Accept-Language': locale ?? 'de',
        Authorization: `Bearer ${process.env.API_TOKEN}`,
      },
    }).then(res => {
      if (res.ok) {
        return res.json();
      } else {
        throw new Error(res.status.toString());
      }
    });
    return { props: { product: product } };
  } catch (e) {
    return checkApiStatus(e.message);
  }
};

export default ProductDetailsPage;
